/*
  <scroll-sequence>: scroll-driven image sequence for the home hero.

  Desktop (≥ 990px) and no reduced-motion preference: the section becomes
  --scroll-length tall, its content sticks to the viewport and the scroll
  position scrubs through `data-count` frames drawn on a canvas.
  Frames are loaded progressively (first frame, every 4th, then the rest);
  the nearest loaded frame is drawn meanwhile.

  Everywhere else the poster image (or the mobile loop video) stays visible.
*/
if (!customElements.get('scroll-sequence')) {
  class ScrollSequence extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('.hero__canvas');
      this.poster = this.querySelector('.hero__poster');
      this.acts = Array.from(this.querySelectorAll('[data-act]'));
      this.pattern = this.dataset.frames || '';
      this.count = parseInt(this.dataset.count, 10) || 0;
      this.pad = parseInt(this.dataset.pad, 10) || 0;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.desktop = window.matchMedia('(min-width: 990px)');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.evaluate = this.evaluate.bind(this);
      this.desktop.addEventListener('change', this.evaluate);
      this.reduced.addEventListener('change', this.evaluate);
      this.evaluate();

      window.addEventListener('scroll', this.onHint = () => {
        this.classList.toggle('is-scrolled', window.scrollY > 40);
      }, { passive: true });
    }

    disconnectedCallback() {
      this.disable();
      this.desktop?.removeEventListener('change', this.evaluate);
      this.reduced?.removeEventListener('change', this.evaluate);
      window.removeEventListener('scroll', this.onHint);
    }

    get canRun() {
      return this.count > 1 && this.pattern.includes('{n}') && this.canvas && this.desktop.matches && !this.reduced.matches;
    }

    evaluate() {
      if (this.canRun) this.enable();
      else this.disable();
    }

    frameUrl(index) {
      const number = String(index + 1).padStart(this.pad, '0');
      return this.pattern.replace('{n}', number);
    }

    enable() {
      if (this.enabled) return;
      this.enabled = true;
      this.classList.add('is-sequence');
      this.context = this.canvas.getContext('2d');
      this.frames = this.frames || new Array(this.count);
      this.loaded = this.loaded || new Array(this.count).fill(false);
      this.current = -1;

      this.loadFrame(0).then(() => {
        this.canvas.hidden = false;
        this.onResize();
        if (this.poster) this.poster.style.visibility = 'hidden';
      });
      this.preload();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.onScroll();
    }

    disable() {
      if (!this.enabled) {
        this.setAct(this.acts.length - 1);
        return;
      }
      this.enabled = false;
      this.classList.remove('is-sequence');
      if (this.canvas) this.canvas.hidden = true;
      if (this.poster) this.poster.style.visibility = '';
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.setAct(this.acts.length - 1);
    }

    loadFrame(index) {
      if (this.frames[index]) return this.frames[index].ready;
      const image = new Image();
      image.decoding = 'async';
      const ready = new Promise((resolve) => {
        image.onload = () => { this.loaded[index] = true; resolve(); if (index === this.target) this.draw(index); };
        image.onerror = resolve;
      });
      image.src = this.frameUrl(index);
      this.frames[index] = { image, ready };
      return ready;
    }

    async preload() {
      // Coarse pass first so scrubbing looks right early, then fill the gaps.
      const order = [];
      for (let i = 0; i < this.count; i += 4) order.push(i);
      for (let i = 0; i < this.count; i += 1) if (i % 4 !== 0) order.push(i);
      const batchSize = 6;
      for (let i = 0; i < order.length; i += batchSize) {
        if (!this.enabled) return;
        await Promise.all(order.slice(i, i + batchSize).map((index) => this.loadFrame(index)));
      }
    }

    nearestLoaded(index) {
      for (let offset = 0; offset < this.count; offset += 1) {
        if (this.loaded[index - offset]) return index - offset;
        if (this.loaded[index + offset]) return index + offset;
      }
      return -1;
    }

    onResize() {
      if (!this.enabled) return;
      const rect = this.canvas.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(rect.width * ratio);
      this.canvas.height = Math.round(rect.height * ratio);
      this.current = -1;
      this.draw(this.target ?? 0);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        if (!this.enabled) return;
        const rect = this.getBoundingClientRect();
        const distance = rect.height - window.innerHeight;
        const progress = distance > 0 ? Math.min(Math.max(-rect.top / distance, 0), 1) : 0;
        this.target = Math.round(progress * (this.count - 1));
        this.draw(this.target);
        if (this.acts.length) this.setAct(Math.min(this.acts.length - 1, Math.floor(progress * this.acts.length)));
      });
    }

    draw(index) {
      const frameIndex = this.nearestLoaded(index);
      if (frameIndex < 0 || frameIndex === this.current) return;
      const { image } = this.frames[frameIndex];
      const { width, height } = this.canvas;
      if (!width || !height) return;
      // object-fit: cover
      const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
      const drawWidth = image.naturalWidth * scale;
      const drawHeight = image.naturalHeight * scale;
      this.context.clearRect(0, 0, width, height);
      this.context.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
      this.current = frameIndex;
    }

    setAct(index) {
      if (index === this.activeAct) return;
      this.activeAct = index;
      this.acts.forEach((act, i) => act.classList.toggle('is-active', i === index));
    }
  }
  customElements.define('scroll-sequence', ScrollSequence);
}
